/**
 * 顶部固定锚点转跳条 (PC + H5)
 * - header-anchor-item：单个锚点，负责把展示名映射到目标分区并生成 #id 跳转
 * - header-anchor-container：容器，负责跟随顶部已固定元素定位、滚动高亮、加购按钮
 *
 * H5 交互：顶部已固定的 announcement-bar / header 在向下滚动时会被主题加上 .scroll-hide
 * 并向上位移隐藏；本锚点条实时读取这些元素的可见底边，从而在“展示态”贴在它们下方，
 * 在“隐藏态”平滑滑到页面最顶部。
 */
(function () {
  'use strict';

  // 单个锚点项：把配置的 class + 第几个元素 解析为目标 DOM，并写入 <a href="#id">
  class HeaderAnchorItem extends HTMLElement {
    constructor() {
      super();
      this.selectorStr = this.getAttribute('selector-str');
      this.nthDom = parseInt(this.getAttribute('nth-dom'), 10) || 1;
      this.rewriteId = this.getAttribute('rewrite-id');
      this.targetDom = null;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.targetDom = this.getTargetDom();
      if (!this.targetDom) return;

      if (this.rewriteId && !this.targetDom.getAttribute('id')) {
        this.targetDom.setAttribute('id', this.rewriteId);
      }
      this.rewriteAEle();
    }

    getTargetDom() {
      try {
        if (!this.selectorStr || this.nthDom - 1 < 0) return null;
        const all = document.querySelectorAll(this.selectorStr);
        if (!all || all.length === 0) return null;
        return all[this.nthDom - 1] || null;
      } catch (error) {
        console.error('[header-anchor] getTargetDom error:', error);
        return null;
      }
    }

    rewriteAEle() {
      const aEle = this.querySelector('a');
      const targetId = this.targetDom && this.targetDom.getAttribute('id');
      if (!aEle || !targetId) return;
      aEle.setAttribute('href', '#' + targetId);
    }
  }

  if (!window.customElements.get('header-anchor-item')) {
    customElements.define('header-anchor-item', HeaderAnchorItem);
  }

  // 容器：定位跟随、滚动高亮、锦点平滑转跳、加购跳转
  class HeaderAnchorContainer extends HTMLElement {
    constructor() {
      super();
      this.hadFixedTopClassArr = this.getAttribute('had-fixed-top-class-arr') || '';
      this.dynamicFixedTopSelectorArr = this.getAttribute('dynamic-fixed-top-selector-arr') || '';
      this.scrollDuration = parseInt(this.getAttribute('scroll-duration'), 10);
      if (!(this.scrollDuration >= 0)) this.scrollDuration = 2000;
      this.section = null;
      this.observer = null;
      this.followUntil = 0;
      this.rafId = null;
      this.scrollRAF = null;
      this.onScroll = this.onScroll.bind(this);
      this.tick = this.tick.bind(this);
      this.cancelAnimatedScroll = this.cancelAnimatedScroll.bind(this);
      this.resetCtaLoading = this.resetCtaLoading.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('[id^="shopify-section-"]');
      this.fixedSelectors = this.hadFixedTopClassArr
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      this.dynamicSelectors = this.dynamicFixedTopSelectorArr
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      this.items = Array.from(this.querySelectorAll('header-anchor-item'));
      this.setupCta();
      this.setupAnchorScroll();
      this.updatePosition();
      this.updateActive();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      window.addEventListener('pageshow', this.resetCtaLoading);
      // 用户主动滚动时终止锦点动画滚动
      window.addEventListener('wheel', this.cancelAnimatedScroll, { passive: true });
      window.addEventListener('touchstart', this.cancelAnimatedScroll, { passive: true });
      this.setupDynamicObserver();
      this.setupFixedObserver();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('pageshow', this.resetCtaLoading);
      window.removeEventListener('wheel', this.cancelAnimatedScroll);
      window.removeEventListener('touchstart', this.cancelAnimatedScroll);
      if (this.observer) this.observer.disconnect();
      if (this.fixedObserver) this.fixedObserver.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.scrollRAF) cancelAnimationFrame(this.scrollRAF);
    }

    onScroll() {
      // 滚动结束后再持续跟随一小段时间，覆盖顶部条 transform 收尾动画，避免留白/抖动
      this.followUntil = Date.now() + 400;
      if (!this.rafId) this.rafId = requestAnimationFrame(this.tick);
    }

    tick() {
      this.updatePosition();
      this.updateActive();
      if (Date.now() < this.followUntil) {
        this.rafId = requestAnimationFrame(this.tick);
      } else {
        this.rafId = null;
      }
    }

    // 判断顶部固定元素当前是否处于收起/隐藏态
    // 注意：本主题收起时是 opacity:0 + transform 位移(并未完全移出视口，rect.bottom 可能残留正值)，
    // 因此不能只看 rect.bottom，必须显式识别隐藏态并将其贡献视为 0。
    isHidden(el) {
      if (el.classList.contains('scroll-hide')) return true;
      const cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') return true;
      if (parseFloat(cs.opacity) === 0) return true;
      return false;
    }

    // 顶部条位置：只累加“当前展示态”固定元素的可见底边；全部收起时归零(top=0)
    updatePosition() {
      if (!this.section) return;
      let top = 0;
      for (let i = 0; i < this.fixedSelectors.length; i += 1) {
        const el = document.querySelector(this.fixedSelectors[i]);
        if (!el || this.isHidden(el)) continue;
        const bottom = el.getBoundingClientRect().bottom;
        if (bottom > top) top = bottom;
      }
      console.log('Top值:', Math.max(0, Math.round(top)) + 'px');
      this.section.style.top = Math.max(0, Math.round(top)) + 'px';
    }

    // 滚动高亮：当前视口靠上的目标分区对应的锚点高亮
    updateActive() {
      if (!this.items.length) return;
      const triggerPoint = window.innerHeight * 0.6;
      let currentId = '';

      const targets = this.items
        .map((item) => {
          try {
            const sel = item.getAttribute('selector-str');
            const nth = parseInt(item.getAttribute('nth-dom'), 10) || 1;
            return document.querySelectorAll(sel)[nth - 1] || null;
          } catch (error) {
            return null;
          }
        })
        .filter(Boolean);

      for (let i = targets.length - 1; i >= 0; i -= 1) {
        if (targets[i].getBoundingClientRect().top <= triggerPoint) {
          currentId = targets[i].getAttribute('id') || '';
          break;
        }
      }

      this.items.forEach((item) => {
        const link = item.querySelector('a');
        const href = link && link.getAttribute('href');
        const isActive = href && href.slice(1) === currentId && currentId !== '';
        item.classList.toggle('active', Boolean(isActive));
      });
    }

    // 加购按钮：默认取产品页 product.selected_or_first_available_variant，
    // 可在分区配置中指定“加购的目标变体”。功能写死为“转跳到购物车页面并加购”。
    setupCta() {
      this.cta = this.querySelector('[data-anchor-cta]');
      if (!this.cta) return;
      this.cartAddBase = this.cta.getAttribute('data-cart-add-base') || '/cart/add';
      this.cartReturnTo = this.cta.getAttribute('data-cart-return-to') || '/cart';
      this.variantId = this.cta.getAttribute('data-variant-id') || '';

      this.updateCtaHref();
      this.cta.addEventListener('click', (event) => {
        if (this.ctaLoading) {
          event.preventDefault();
          return;
        }
        const url = this.buildCartUrl();
        if (!url) {
          event.preventDefault();
          return;
        }
        this.cta.setAttribute('href', url);
        this.setCtaLoading();
      });
    }

    // 进入加载态：显示 spinner 并锁定按钮，跳转前不可再次点击
    setCtaLoading() {
      this.ctaLoading = true;
      this.cta.classList.add('is-loading');
      this.cta.setAttribute('aria-busy', 'true');
    }

    resetCtaLoading() {
      if (!this.cta) return;
      this.ctaLoading = false;
      this.cta.classList.remove('is-loading');
      this.cta.removeAttribute('aria-busy');
    }

    updateCtaHref() {
      const url = this.buildCartUrl();
      if (url) this.cta.setAttribute('href', url);
    }

    // 数量：优先 hero-video-early-bird，其次 product-buy-box，再次 main-product(.quantity__input)，默认 1
    getQuantity() {
      const input =
        document.querySelector('.hero-video-early-bird__quantity-input') ||
        document.querySelector('.product-buy-box__quantity-input') ||
        document.querySelector('.product-form__quantity .quantity__input');
      const qty = input ? parseInt(input.value, 10) : NaN;
      return qty > 0 ? qty : 1;
    }

    buildCartUrl() {
      const variantId = (this.variantId || '').trim();
      if (!/^\d+$/.test(variantId) || variantId === '0') return '';
      // 通过拼接 /cart/add?id=[Variant_ID]&quantity=[数量]&return_to=/cart 加购并跳转购物车页
      const base = this.cartAddBase.replace(/\/$/, '');
      return (
        base +
        '?id=' +
        encodeURIComponent(variantId) +
        '&quantity=' +
        encodeURIComponent(this.getQuantity()) +
        '&return_to=' +
        encodeURIComponent(this.cartReturnTo)
      );
    }

    // 锚点转跳：拦截默认瞬间跳转，改为带时长(默认 2s)的平滑滚动过渡
    setupAnchorScroll() {
      this.addEventListener('click', (event) => {
        const link = event.target.closest('header-anchor-item a');
        if (!link || !this.contains(link)) return;
        const href = link.getAttribute('href') || '';
        if (href.charAt(0) !== '#' || href.length < 2) return;
        const target = document.getElementById(href.slice(1));
        if (!target) return;
        event.preventDefault();
        this.animateScrollTo(target);
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', href);
        }
      });
    }

    cancelAnimatedScroll() {
      if (this.scrollRAF) {
        cancelAnimationFrame(this.scrollRAF);
        this.scrollRAF = null;
      }
    }

    animateScrollTo(target) {
      this.cancelAnimatedScroll();

      const startY = window.pageYOffset || document.documentElement.scrollTop;
      // 让目标分区顶部停在锚点条底部下方
      const barBottom = this.section ? this.section.getBoundingClientRect().bottom : 0;
      const targetTop = target.getBoundingClientRect().top;
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      let destination = startY + targetTop - barBottom;
      destination = Math.max(0, Math.min(destination, maxScroll));

      const distance = destination - startY;
      if (Math.abs(distance) < 1) return;

      const duration = this.scrollDuration;
      if (duration <= 0) {
        window.scrollTo(0, destination);
        return;
      }

      const startTime = performance.now();
      const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

      const step = (now) => {
        const progress = Math.min(1, (now - startTime) / duration);
        window.scrollTo(0, startY + distance * easeInOutCubic(progress));
        if (progress < 1) {
          this.scrollRAF = requestAnimationFrame(step);
        } else {
          this.scrollRAF = null;
        }
      };
      this.scrollRAF = requestAnimationFrame(step);
    }

    // 直接监听顶部固定元素的 class/style 变化(收起/展开)，避免依赖主题滚动时机导致 top 更新滞后
    setupFixedObserver() {
      if (!this.fixedSelectors.length) return;
      this.fixedObserver = new MutationObserver(() => this.updatePosition());
      this.fixedSelectors.forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) {
          this.fixedObserver.observe(el, {
            attributes: true,
            attributeFilter: ['class', 'style'],
          });
        }
      });
    }

    setupDynamicObserver() {
      if (!this.dynamicSelectors.length) return;
      const check = () => {
        const appeared = this.dynamicSelectors.some((sel) => document.querySelector(sel));
        if (appeared) this.updatePosition();
      };
      this.observer = new MutationObserver(check);
      this.observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(check, 500);
    }
  }

  if (!window.customElements.get('header-anchor-container')) {
    customElements.define('header-anchor-container', HeaderAnchorContainer);
  }
})();
